import { com } from '@eidu/web'
import { Checkbox, Group, iconSizes, Loader, Select, Text, TextInput, UnlabeledField } from '@common/ui'
import { IconAlertTriangle } from '@common/ui/icons'
import TrimmedTextInput from './TrimmedTextInput'
import EntityTypeInput from './EntityTypeInput'
import { isReferenceType } from '../misc/ReferenceTypeUtil'
import TypeValidationError = com.eidu.entity.validation.TypeValidationError
import EntityType = com.eidu.entity.type.EntityType
import FieldType = com.eidu.entity.field.FieldType
import EntityTypeId = com.eidu.entity.type.EntityTypeId

export type FieldInputProps = {
  availableEntityTypes: readonly EntityType[]
  disabled: boolean
  editable: boolean
  fieldType: FieldType
  handleFieldTypeChange: (value: string) => Promise<void>
  handleReferenceTypesChange: (referenceTypes: readonly EntityTypeId[]) => Promise<void>
  handleRequiredChange: () => Promise<void>
  handleFieldNameChange?: (newName: string) => Promise<void>
  idPrefix: string
  name: string
  referenceTypesError: boolean
  referenceTypes: readonly EntityTypeId[]
  required: boolean
  updating: boolean
  errors: readonly TypeValidationError[] | undefined
  editableName?: boolean
}

const availableFieldTypeValues = FieldType.values().filter((it) => it !== FieldType.Null)

const FieldInput = ({
  availableEntityTypes,
  disabled,
  editable,
  fieldType,
  handleFieldTypeChange,
  handleReferenceTypesChange,
  handleRequiredChange,
  handleFieldNameChange,
  idPrefix,
  name,
  referenceTypes,
  referenceTypesError,
  required,
  updating,
  errors,
  editableName = false,
}: FieldInputProps) => (
  <>
    {editableName && handleFieldNameChange && (
      <Group gap={0} mb="md" wrap="nowrap">
        <TrimmedTextInput
          label="Field name"
          value={name}
          onChange={(event) => handleFieldNameChange(event.target.value)}
          style={{ flex: 1 }}
        />
        <div style={{ flex: 1, paddingLeft: 16 }} />
      </Group>
    )}
    <Group gap={0} align="flex-start" wrap="nowrap">
      {editable ? (
        <Select
          style={{ flex: 1 }}
          disabled={disabled}
          name={name}
          label={name}
          value={fieldType.name}
          data={availableFieldTypeValues.map((option) => ({ value: option.name, label: option.name }))}
          allowDeselect={false}
          rightSection={updating ? <Loader size="xs" /> : undefined}
          onChange={(value) => {
            if (value !== null) handleFieldTypeChange(value)
          }}
        />
      ) : (
        <TextInput label={name} id={name} disabled value={fieldType.name} />
      )}
      <UnlabeledField style={{ paddingLeft: 16, flex: 1 }}>
        <Checkbox checked={required} disabled={!editable} onChange={() => handleRequiredChange()} label="Required" />
      </UnlabeledField>
    </Group>
    {isReferenceType(fieldType) && (
      <Group gap={0} align="center" wrap="nowrap" mt="lg">
        <div style={{ flex: 0.1 }} />
        <div style={{ flex: 0.9 }}>
          <EntityTypeInput
            label={`Valid types for ${name}`}
            labelId={`${idPrefix}-reftype-${name}`}
            disabled={disabled || !editable}
            availableEntityTypes={availableEntityTypes}
            selectedEntityTypes={referenceTypes}
            setSelectedEntityTypes={handleReferenceTypesChange}
            error={referenceTypesError}
          />
        </div>
        <div style={{ flex: 1, paddingLeft: 16 }} />
      </Group>
    )}
    {errors && (
      <Text mt="xs">
        <IconAlertTriangle
          size={iconSizes.sm}
          stroke={1.5}
          color="var(--trellis-color-caution)"
          style={{ marginInline: 8, verticalAlign: 'middle' }}
        />
        Validation failed: {errors.map((it) => it.message).join(', ')}
      </Text>
    )}
  </>
)

export default FieldInput
